function isObject(value: unknown): value is Record<string, any> {
  return typeof value === "object" && value !== null;
}

function extractFromValidationDetail(detail: unknown): string | null {
  if (!Array.isArray(detail)) return null;

  const messages = detail
    .map((item) => {
      if (!isObject(item)) return null;

      const field = Array.isArray(item.loc)
        ? item.loc.filter((x: unknown) => x !== "body").join(".")
        : null;

      const msg = item.msg || item.message;

      if (!msg) return null;

      return field ? `${field}: ${msg}` : String(msg);
    })
    .filter(Boolean);

  return messages.length > 0 ? messages.join("\n") : null;
}

export function extractApiErrorMessage(data: unknown): string | null {
  if (!isObject(data)) return null;

  const detail = data.detail;

  if (typeof detail === "string") {
    return detail;
  }

  const validationMessage = extractFromValidationDetail(detail);
  if (validationMessage) {
    return validationMessage;
  }

  if (isObject(detail)) {
    if (typeof detail.message === "string") {
      return detail.details?.length
        ? `${detail.message}\n${detail.details.join("\n")}`
        : detail.message;
    }

    if (isObject(detail.error) && typeof detail.error.message === "string") {
      return detail.error.message;
    }
  }

  if (isObject(data.error) && typeof data.error.message === "string") {
    return data.error.message;
  }

  if (typeof data.message === "string") {
    return data.message;
  }

  return null;
}

function fallbackByStatus(status: number, fallback: string): string {
  if (status === 400) return "Nieprawidłowe dane żądania.";
  if (status === 401) return "Sesja wygasła. Zaloguj się ponownie.";
  if (status === 403) return "Brak uprawnień do wykonania tej operacji.";
  if (status === 404) return "Nie znaleziono wskazanego zasobu.";
  if (status === 409) return "Operacja nie może zostać wykonana z powodu konfliktu danych.";
  if (status === 413) return "Plik jest za duży.";
  if (status >= 500) return "Wystąpił błąd serwera. Spróbuj ponownie później.";

  return fallback;
}

export async function getApiErrorMessage(
  res: Response,
  fallback: string
): Promise<string> {
  const contentType = res.headers.get("content-type") || "";

  try {
    if (contentType.includes("application/json")) {
      const data = await res.json();
      return extractApiErrorMessage(data) || fallbackByStatus(res.status, fallback);
    }

    const text = await res.text();
    return text || fallbackByStatus(res.status, fallback);
  } catch {
    return fallbackByStatus(res.status, fallback);
  }
}

export function normalizeFetchError(error: unknown, fallback: string): Error {
  if (error instanceof Error) {
    if (
      error.message === "Failed to fetch" ||
      error.message.includes("NetworkError")
    ) {
      return new Error(
        "Nie udało się połączyć z serwerem. Sprawdź połączenie lub spróbuj ponownie."
      );
    }

    return error;
  }

  return new Error(fallback);
}